// PayeePickerModal.jsx — THE shared find-and-link payee picker (spec §6).
// Searches live masters + their aliases via searchsuppliers.sz (org-scoped,
// inert masters excluded server-side). Selection only — no create path.
// onSelect(record, allResults): record = { id, name, match_name, match_source,
// street, city, postal_code, country, rank, sim, location_id, location_name }.
import { useEffect, useRef, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import useFetchData from "./useFetchDatav2";

export default function PayeePickerModal({
   show,
   onClose,
   onSelect,
   title = "Select payee",
   initialQuery = "",
   excludeMasterId = null,
   helperText = "",
   zIndex = null,
}) {
   const { fetchData } = useFetchData();
   const [query, setQuery] = useState(initialQuery);
   const [results, setResults] = useState([]);
   const [isSearching, setIsSearching] = useState(false);
   const [selfOnly, setSelfOnly] = useState(false);
   const debounceRef = useRef(null);

   const runSearch = async (term) => {
      const normalized = String(term || "").trim();
      if (normalized.length < 2) {
         setResults([]);
         setSelfOnly(false);
         return;
      }
      setIsSearching(true);
      try {
         const formData = new FormData();
         formData.append("_q", normalized);
         const { responseData } = await fetchData("/searchsuppliers.sz", "POST", formData, "form-data");
         const payload = responseData?.st === 1 && Array.isArray(responseData?.payload)
            ? responseData.payload
            : [];
         const filtered = excludeMasterId != null
            ? payload.filter((r) => Number(r?.id) !== Number(excludeMasterId))
            : payload;
         setSelfOnly(payload.length > 0 && filtered.length === 0);
         setResults(filtered);
      } catch {
         setResults([]);
         setSelfOnly(false);
      } finally {
         setIsSearching(false);
      }
   };

   useEffect(() => {
      if (!show) return;
      setQuery(initialQuery);
      runSearch(initialQuery);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [show, initialQuery]);

   const handleChange = (value) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runSearch(value), 300);
   };

   // zIndex: callers that live inside a higher-stacked container (the fullscreen
   // configure modal sits at 2000) must lift the picker above it.
   return (
      <Modal show={show} onHide={onClose} centered style={zIndex != null ? { zIndex } : undefined}>
         <Modal.Header closeButton>
            <Modal.Title className="fs-6">{title}</Modal.Title>
         </Modal.Header>
         <Modal.Body>
            {helperText && <p className="text-secondary small mb-2">{helperText}</p>}
            <Form.Control
               size="sm"
               autoFocus
               placeholder="Search suppliers and their known names…"
               value={query}
               onChange={(e) => handleChange(e.target.value)}
            />
            <div className="border rounded mt-2" style={{ maxHeight: 280, overflowY: "auto" }}>
               {isSearching ? (
                  <div className="px-3 py-2 small text-secondary">Searching…</div>
               ) : results.length > 0 ? (
                  results.map((r, i) => {
                     const addr = [r?.street, r?.city, r?.postal_code].filter(Boolean).join(", ");
                     return (
                        <button
                           key={`payee-${r?.id ?? i}`}
                           type="button"
                           className="dropdown-item text-wrap py-2"
                           onClick={() => onSelect(r, results)}
                        >
                           <div className="fw-semibold">{r?.name || "Unnamed supplier"}</div>
                           {r?.match_source === "alias" && r?.match_name && (
                              <div className="text-secondary" style={{ fontSize: "0.75rem" }}>
                                 known as "{r.match_name}"
                                 {r?.location_name ? ` · ${r.location_name}` : ""}
                              </div>
                           )}
                           {addr && <div className="text-secondary" style={{ fontSize: "0.75rem" }}>{addr}</div>}
                        </button>
                     );
                  })
               ) : (
                  <div className="px-3 py-2 small text-secondary">
                     {selfOnly
                        ? "Only this supplier itself matched that search — try a different spelling or another part of the name."
                        : "No supplier found. Suppliers come from the ERP sync (runs every 2 hours) — there is no manual create here."}
                  </div>
               )}
            </div>
         </Modal.Body>
      </Modal>
   );
}
